import { parsePln, plnToInput } from '../../catalog/money';
import { warsawParts } from '../../events/labels';
import { formatDecimal, parseScaled } from '../../ui/numbers';
import type { OfferDiscount } from './types';

/** „Oferta łączona” (owner 2026-10-30): small pure helpers of the screens. */

export const offerUrl = (id: string): string => `/wyceny/oferty/${id}`;

const ONE_CLIENT = 'Oferta łączona jest dla jednego klienta';

/** Whether events picked in the calendar can make one offer: at least two, every one of one client. */
export function combineCheck(events: ReadonlyArray<{ name: string; contractorId?: string | null | undefined }>, clientNames: ReadonlyMap<string, string>): { ok: true; clientId: string } | { ok: false; message: string } {
  if (events.length < 2) return { ok: false, message: 'Zaznacz co najmniej dwa wydarzenia (⌘-klik albo Ctrl-klik).' };
  const bare = events.find((e) => !e.contractorId);
  if (bare) return { ok: false, message: `Wydarzenie „${bare.name}” nie ma klienta. ${ONE_CLIENT} — ustaw go w wydarzeniu.` };
  const clients = [...new Set(events.map((e) => e.contractorId!))];
  if (clients.length > 1) return { ok: false, message: `Zaznaczone wydarzenia mają różnych klientów (${clients.map((c) => clientNames.get(c) ?? 'nieznany').join(', ')}). ${ONE_CLIENT}.` };
  return { ok: true, clientId: clients[0]! };
}

/** The end of the offer in its fixed order — shared with the offer PDF, so the screen and the PDF never differ. */
export { discountNote, totalsRows, type TotalsRow } from '@app/shared/offer-print';

/** A discount typed by hand: % (two decimals at most) to bp, złoty to grosze; empty is 0. Throws a Polish message. */
export function discountFromInput(kind: OfferDiscount['kind'], text: string): number {
  if (text.trim() === '') return 0;
  if (kind === 'amount') return parsePln(text);
  const bp = parseScaled(text.replace(/\s*%\s*$/, ''), 2, { maxIntegerDigits: 3 });
  if (bp === null || bp < 0 || bp > 10000) throw new Error('Rabat musi wynosić od 0% do 100%');
  return bp;
}

export const discountToInput = (d: OfferDiscount): string => (d.kind === 'amount' ? plnToInput(d.value) : formatDecimal(d.value / 100));

const MONTHS = ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'];

/** The date stamp of an event in the offer: its Warsaw day number and the short month. */
export function dateStamp(iso: string): { day: string; month: string } {
  const [, m, d] = warsawParts(iso).day.split('-');
  return { day: String(Number(d)), month: MONTHS[Number(m) - 1]! };
}

/** „5 lis 2026”: a Warsaw day for the lists. */
export function formatDay(iso: string): string {
  const { day, month } = dateStamp(iso);
  return `${day} ${month} ${warsawParts(iso).day.slice(0, 4)}`;
}
